import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFormat } from '../../context/FormatContext';
import ConfirmModal from '../common/ConfirmModal';

const { FiUploadCloud, FiFileText, FiRefreshCw, FiInfo, FiLock } = FiIcons;

const LOCKED_FIELD_CLASSES = 'bg-slate-50 text-slate-500 cursor-not-allowed';

const UploadStep = () => {
  const {
    pipelineState, setPipelineState, uploadAndAnalyze, updateManuscript, loading,
    activeProject, manuscripts, fetchManuscripts,
  } = useFormat();
  const fileInputRef = useRef(null);
  const [showFileWarning, setShowFileWarning] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  // Is this an existing manuscript? Covers both "Edit / Revise" from the dashboard
  // and stepping back to Step 1 from a project opened by clicking its card.
  const existingId = pipelineState?.existingManuscriptId
    || (activeProject?.id && activeProject.id !== 'new' ? activeProject.id : null);
  const isRevision = !!existingId;

  // Always re-check the lock when this step opens: an admin may have locked the
  // project after the dashboard list was loaded.
  const [lockChecked, setLockChecked] = useState(!isRevision);
  useEffect(() => {
    if (!isRevision) {
      setLockChecked(true);
      return undefined;
    }
    let cancelled = false;
    setLockChecked(false);
    Promise.resolve(typeof fetchManuscripts === 'function' ? fetchManuscripts() : null)
      .catch(() => {})
      .finally(() => { if (!cancelled) setLockChecked(true); });
    return () => { cancelled = true; };
  }, [existingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Book Metadata locked by an admin? (manuscripts.metadata_locked = 1)
  // The server enforces this too; the form just makes it clear to the author.
  const latestProject = (manuscripts || []).find(m => String(m.id) === String(existingId)) || activeProject;
  const isLocked = isRevision && Number(latestProject?.metadata_locked) === 1;
  // Fields stay read-only for the moment it takes to check the lock.
  const fieldsReadOnly = isLocked || (isRevision && !lockChecked);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['docx', 'doc'].includes(ext)) {
      alert('Please upload a valid .docx or .doc file.');
      return;
    }

    if (isRevision) {
      setPendingFile(file);
      setShowFileWarning(true);
    } else {
      setPipelineState(prev => ({ ...prev, file }));
    }
  };

  const confirmFileReplacement = () => {
    if (pendingFile) {
      setPipelineState(prev => ({ ...prev, file: pendingFile }));
      setPendingFile(null);
    }
    setShowFileWarning(false);
  };

  const cancelFileReplacement = () => {
    setPendingFile(null);
    setShowFileWarning(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleMetadataChange = (field, value) => {
    if (fieldsReadOnly) return;
    setPipelineState(prev => ({
      ...prev,
      metadata: { ...prev.metadata, [field]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isRevision) {
      if (!lockChecked) return;
      const formData = new FormData();
      formData.append('manuscript_id', existingId);
      // Locked projects send no metadata; the server keeps the saved values.
      if (!isLocked) {
        Object.keys(pipelineState.metadata).forEach(key => {
          formData.append(key, pipelineState.metadata[key]);
        });
      }
      if (pipelineState.file && pipelineState.file instanceof File) {
        formData.append('file', pipelineState.file);
      }
      try {
        await updateManuscript(formData);
        const { formatService } = await import('../../services/api');
        const analysisRes = await formatService.analyzeManuscript(existingId);
        setPipelineState(prev => ({
          ...prev,
          step: 2,
          analysis: { score: analysisRes.health_score, report: analysisRes.analysis_report },
          structure: analysisRes.structure,
        }));
      } catch (err) {
        alert('Update failed: ' + err.message);
      }
      return;
    }

    if (!pipelineState.file) {
      alert('Please select a manuscript file.');
      return;
    }
    const formData = new FormData();
    formData.append('file', pipelineState.file);
    Object.keys(pipelineState.metadata).forEach(key => {
      formData.append(key, pipelineState.metadata[key]);
    });
    uploadAndAnalyze(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto py-8"
    >
      <div className="text-center mb-10">
        <h2 className="text-3xl font-serif text-slate-800 mb-3">
          {isRevision ? 'Revise Manuscript' : 'Upload Manuscript'}
        </h2>
        <p className="text-slate-500">
          {isRevision
            ? 'Update your book details or upload a new file to replace the existing manuscript.'
            : 'Provide your book details and upload your Word document for analysis.'}
        </p>
      </div>

      {isRevision && (
        <div className="flex items-start space-x-3 bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-8">
          <SafeIcon icon={FiInfo} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-800 font-bold mb-1">Revising an existing manuscript</p>
            <p className="text-sm text-blue-700">
              {isLocked
                ? 'Your Book Metadata has been locked by your publisher, so it can’t be edited here. You can still upload a new file to replace the existing Word document, which will reset analysis & export records.'
                : 'Your current metadata is pre-filled. You may update any field. Uploading a new file will replace the existing Word document and reset analysis & export records.'}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
            <h3 className="text-lg font-bold text-slate-800">Book Metadata</h3>
            {isLocked && (
              <span
                className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-[10px] font-black uppercase tracking-widest"
                title="Your publisher has locked these details. Contact them if something needs to change."
              >
                <SafeIcon icon={FiLock} />
                <span>Locked by your publisher</span>
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Book Title *</label>
              <input
                type="text"
                required
                value={pipelineState.metadata.title}
                onChange={e => handleMetadataChange('title', e.target.value)}
                readOnly={fieldsReadOnly}
                aria-readonly={fieldsReadOnly}
                className={`form-input ${fieldsReadOnly ? LOCKED_FIELD_CLASSES : ''}`}
                placeholder="e.g. The Great Adventure"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Author Name *</label>
              <input
                type="text"
                required
                value={pipelineState.metadata.author}
                onChange={e => handleMetadataChange('author', e.target.value)}
                readOnly={fieldsReadOnly}
                aria-readonly={fieldsReadOnly}
                className={`form-input ${fieldsReadOnly ? LOCKED_FIELD_CLASSES : ''}`}
                placeholder="e.g. Jane Author"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Publish Imprint / Publisher</label>
              <input
                type="text"
                value={pipelineState.metadata.publisher}
                onChange={e => handleMetadataChange('publisher', e.target.value)}
                readOnly={fieldsReadOnly}
                aria-readonly={fieldsReadOnly}
                className={`form-input ${fieldsReadOnly ? LOCKED_FIELD_CLASSES : ''}`}
                placeholder="e.g. Graceway Publishing"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Language</label>
              <select
                value={pipelineState.metadata.language}
                onChange={e => handleMetadataChange('language', e.target.value)}
                disabled={fieldsReadOnly}
                className={`form-select ${fieldsReadOnly ? LOCKED_FIELD_CLASSES : ''}`}
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Description (Optional)</label>
              <textarea
                value={pipelineState.metadata.description || ''}
                onChange={e => handleMetadataChange('description', e.target.value)}
                readOnly={fieldsReadOnly}
                aria-readonly={fieldsReadOnly}
                className={`form-input resize-none h-24 ${fieldsReadOnly ? LOCKED_FIELD_CLASSES : ''}`}
                placeholder="Briefly describe your book for the metadata..."
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          <h3 className="text-lg font-bold text-slate-800 mb-2 border-b border-slate-100 pb-4">
            {isRevision ? 'Replace Manuscript File (Optional)' : 'Manuscript File'}
          </h3>
          <div
            className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${
              pipelineState.file instanceof File
                ? 'border-primary bg-primary/5'
                : 'border-slate-300 hover:border-primary hover:bg-slate-50'
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".docx,.doc"
              className="hidden"
            />
            {pipelineState.file instanceof File ? (
              <div className="flex flex-col items-center">
                <SafeIcon icon={FiFileText} className="text-4xl text-primary mb-4" />
                <span className="font-bold text-slate-800">{pipelineState.file.name}</span>
                <span className="text-sm text-slate-500 mt-2">Click to replace file</span>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <SafeIcon icon={isRevision ? FiRefreshCw : FiUploadCloud} className="text-4xl text-slate-400 mb-4" />
                <span className="font-bold text-slate-700 mb-1">
                  {isRevision ? 'Click to upload a new Word document' : 'Click to upload Word document'}
                </span>
                <span className="text-sm text-slate-500">.docx or .doc formats accepted</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading || (!isRevision && !pipelineState.file) || (isRevision && !lockChecked)}
            className="bg-primary hover:bg-blue-800 text-white px-8 py-4 rounded-xl font-bold uppercase tracking-widest text-sm transition-all shadow-lg disabled:opacity-50 flex items-center space-x-2"
          >
            {loading ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div><span>Processing...</span></>
            ) : (
              <span>{isRevision ? 'Save & Re-Analyze' : 'Analyze Manuscript'}</span>
            )}
          </button>
        </div>
      </form>

      <ConfirmModal
        isOpen={showFileWarning}
        onClose={cancelFileReplacement}
        onConfirm={confirmFileReplacement}
        variant="warning"
        title="Replace Existing File?"
        message="Uploading a new file will permanently replace the current manuscript. This will reset your conversion progress (Analysis and Chapters) for this book."
        confirmText="Yes, Replace File"
      />
    </motion.div>
  );
};

export default UploadStep;